import { BBox2D } from "./BBox2D";
import { PeliasFeature } from "./PeliasFeature";
import { PeliasQuery } from "./PeliasQuery";
import { sanitize } from "./sanitize";

export interface SearchQuery extends PeliasQuery {
    /**
     * The autocomplete endpoint requires, at minimum, a text parameter, 
     * which should generally be filled by text from a search box typed by an end user.
     */
    text: string,
}

export interface StructuredQuery extends PeliasQuery {
    address?: string
    neighbourhood?: string
    borough?: string
    locality?: string
    county?: string
    region?: string
    postalcode?: string
    country?: string
}

export const PeliasV1Endpoints = {
    /**
     * Autocomplete: https://geocode.earth/docs/forward/autocomplete/#filtering-parameters
     * @param baseUrl default: https://api.geocode.earth
     * @param lang default: en
     */
    autocomplete: async (query: SearchQuery, baseUrl?: string, lang?: string): Promise<PeliasFeature[]> => {
        return v1Get(query, baseUrl, '/autocomplete', lang);
    },

    /**
     * Search Endpoint: https://geocode.earth/docs/forward/search/
     * @param baseUrl default: https://api.geocode.earth
     * @param lang default: en
     */
    search: async (query: SearchQuery, baseUrl?: string, lang?: string): Promise<PeliasFeature[]> => {
        return v1Get(query, baseUrl, '/search', lang);
    },

    /**
     * Structured Geocoding: https://geocode.earth/docs/forward/structured/
     * @param baseUrl default: https://api.geocode.earth
     * @param lang default: en
     */
    searchStructured: async (query: StructuredQuery, baseUrl?: string, lang?: string): Promise<PeliasFeature[]> => {
        return v1Get(query, baseUrl, '/search/structured', lang);
    }
}

async function v1Get(query: Record<string, any>, baseUrl: string | undefined, path: string, lang?: string): Promise<PeliasFeature[]> {
    const search = toSearch(getObj({ api_key: "ge-ad1fb354c6ac9f2e", ...query }));
    const resp = await fetch((baseUrl || "https://api.geocode.earth") + '/v1' + path + search, { 
        method: "GET",
        headers: { "Accept-Language": lang || "en" }
    });
    const results = await resp.json();
    const features = sanitize(results.features).sort(sortByArea);
    return features;
}

function toSearch(obj: Record<string, string | number>) {
    return '?' + Object.keys(obj).map(key => `${key}=${encodeURIComponent(obj[key])}`).join('&');
}

function getObj(object: Record<string, any>) {
    const result: Record<string, string | number> = {}; 

    for (const key in object) {
        const val = object[key];

        if (Array.isArray(val)) {
            result[key] = val.join(',');

        } else if (typeof val === "object") {
            const child = getObj(val);

            Object.keys(child).forEach(childKey => {
                result[key + '.' + childKey] = child[childKey];
            });

        } else if (val) {
            result[key] = val;
        }
    }

    return result;
}

function sortByArea(a: PeliasFeature, b: PeliasFeature): number {
    return BBox2D.area(b.bbox) - BBox2D.area(a.bbox)
}